import { ref, computed, watch } from "vue";
import { api } from "@/backend";
import { GetAccountResponse as Account } from "@be/api";
import { subscriptionStore } from "./subscriptions";

const rootAcct = ref<Account | undefined>(undefined);
const childAcct = ref<Account | undefined>(undefined);

async function getAccount(
  accountId: number | undefined,
): Promise<Account | undefined> {
  if (accountId === undefined) {
    return Promise.reject();
  }

  try {
    const response = await api.getAccount({ accountId });
    return Promise.resolve(response.data.data);
  } catch (error) {
    console.error("Failed to get account", error);
    return Promise.reject(error);
  }
}

export async function populateRootAccount(accountId: number | undefined) {
  if (accountId === undefined) {
    clearAccount();
    return Promise.reject();
  }

  var currAcct = rootAcct.value;
  if (currAcct && currAcct.accountId === accountId) return Promise.resolve();

  try {
    clearAccount();
    rootAcct.value = await getAccount(accountId);

    return Promise.resolve();
  } catch (error) {
    console.error("Failed to get account", error);
    clearAccount();
    return Promise.reject(error);
  }
}

export function clearAccount() {
  rootAcct.value = undefined;
  clearChildAccount();
}

export function clearChildAccount() {
  childAcct.value = undefined;
}

export async function populateChildAccount(accountId: number | undefined) {
  if (accountId === undefined) {
    clearChildAccount();
    return Promise.reject();
  }

  var currChildAcct = childAcct.value;
  if (currChildAcct && currChildAcct.accountId === accountId)
    return Promise.resolve();

  try {
    childAcct.value = await getAccount(accountId);

    return Promise.resolve();
  } catch (error) {
    console.error("Failed to get child account", error);
    clearChildAccount();
    return Promise.reject(error);
  }
}

export const rootAccount = computed(() => rootAcct.value);

export const contextAccount = computed(() => childAcct.value ?? rootAcct.value);

watch(contextAccount, async () => {
  subscriptionStore.clear();

  // no need to reload subscriptions for the reseller account and child accounts
  if (!isContextAcctReseller.value) await subscriptionStore.init();
});

export const isContextAcctReseller = computed(() => {
  return contextAccount.value?.accountType === "Reseller";
});

export const isSuperReseller = computed(() => {
  return rootAcct.value?.isSuperReseller ?? false;
});

export const isResellerChildAcctContext = computed(() => {
  return !!childAcct.value;
});

export const isAuthedAccountResellerChild = computed(() => {
  return rootAccount.value?.hasParentAccount ?? false;
});

export const hasPaymentMethod = computed(() => {
  if (
    contextAccount.value?.hasParentAccount &&
    rootAccount.value?.hasPaymentMethod
  )
    return true;

  return contextAccount.value?.hasPaymentMethod ?? false;
});

function getBillingType(
  type: string | undefined | null,
): "CreditCard" | "Invoice" | "None" {
  const t = type?.toLowerCase() ?? "";
  if (t === "creditcard") return "CreditCard";
  if (t === "invoice") return "Invoice";
  return "None";
}

export const paymentMethod = computed(() => {
  if (contextAccount.value?.hasParentAccount)
    return getBillingType(rootAccount.value?.billingInfo?.type);

  return getBillingType(contextAccount.value?.billingInfo?.type);
});

// reseller child account that sign in directly should not have access to subscriptions
export const isUserAllowedToCreateSubscriptions = computed(() => {
  return !rootAccount.value?.hasParentAccount;
});

export const shouldShowConnect = computed(() => {
  if (!contextAccount.value || !contextAccount.value.accountAppAccess)
    return true;

  var connectAccess = contextAccount.value.accountAppAccess.find(
    (access) => access.appId === 22,
  );
  return connectAccess && connectAccess.hasAccess === true;
});
